import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import { authService } from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import {
  exportOperationalDashboard,
  getOperationalDashboard,
  OperationalDashboardData,
} from '../../services/operationalAnalyticsService';
import { openAnalyticsPrintWindow } from '../../utils/analyticsExport';
import { localTodayIso } from '../../utils/billingReconciliationExport';
import KpiCardGrid, { KpiCardItem } from '../../components/analytics/KpiCardGrid';
import AnalyticsGroupedBarChart from '../../components/analytics/AnalyticsGroupedBarChart';
import TatCompliancePanel from '../../components/analytics/TatCompliancePanel';
import AnalyticsStudiesTable from '../../components/analytics/AnalyticsStudiesTable';
import AnalyticsExportMenu, {
  AnalyticsExportFormat,
} from '../../components/analytics/AnalyticsExportMenu';
import AnalyticsDashboardSkeleton from '../../components/analytics/AnalyticsDashboardSkeleton';
import { formatDurationMinutes, formatPercent } from '../../components/analytics/chartUtils';
import { AnalyticsDateInput } from '../../utils/analyticsDateUtils';
import '../../components/analytics/analytics-theme.css';

function defaultFromDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function formatRefreshTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

export default function OperationalDashboard() {
  const user = authService.getUser();
  const [from, setFrom] = useState(defaultFromDate);
  const [to, setTo] = useState(localTodayIso);
  const [modality, setModality] = useState('all');
  const [department, setDepartment] = useState('all');
  const [radiologistId, setRadiologistId] = useState('all');
  const [priority, setPriority] = useState('all');
  const [patientType, setPatientType] = useState('all');
  const [facilityId, setFacilityId] = useState('all');
  const [workloadPeriod, setWorkloadPeriod] = useState('daily');
  const [studyStatus, setStudyStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OperationalDashboardData | null>(null);

  const statusFilter = studyStatus !== 'all' ? studyStatus : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await getOperationalDashboard({
      from,
      to,
      facilityId,
      modality,
      department,
      radiologistId,
      priority,
      patientType,
      status: statusFilter,
      workloadPeriod,
      page,
      pageSize,
    });
    if (result.success && result.data) {
      setData(result.data);
    } else {
      setError(result.error || 'Failed to load dashboard');
      setData(null);
    }
    setLoading(false);
  }, [
    from,
    to,
    facilityId,
    modality,
    department,
    radiologistId,
    priority,
    patientType,
    statusFilter,
    workloadPeriod,
    page,
    pageSize,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) {
      return undefined;
    }
    const id = window.setInterval(load, 120000);
    return () => window.clearInterval(id);
  }, [autoRefresh, load]);

  const kpiItems: KpiCardItem[] = useMemo(() => {
    const s = data?.summary;
    return [
      {
        key: 'received',
        label: 'Studies received',
        value: s?.studiesReceived ?? 0,
        clickable: true,
        active: studyStatus === 'received',
        onClick: () => {
          setStudyStatus('received');
          setPage(1);
        },
      },
      {
        key: 'inProgress',
        label: 'In progress',
        value: s?.inProgress ?? 0,
        clickable: true,
        active: studyStatus === 'inProgress',
        onClick: () => {
          setStudyStatus('inProgress');
          setPage(1);
        },
      },
      {
        key: 'awaitingReview',
        label: 'Awaiting review',
        value: s?.awaitingReview ?? 0,
        clickable: true,
        active: studyStatus === 'awaitingReview',
        onClick: () => {
          setStudyStatus('awaitingReview');
          setPage(1);
        },
      },
      {
        key: 'completed',
        label: 'Completed',
        value: s?.completed ?? 0,
        clickable: true,
        active: studyStatus === 'completed',
        onClick: () => {
          setStudyStatus('completed');
          setPage(1);
        },
      },
      {
        key: 'pending',
        label: 'Pending',
        value: s?.pending ?? 0,
        clickable: true,
        active: studyStatus === 'pending',
        onClick: () => {
          setStudyStatus('pending');
          setPage(1);
        },
      },
      {
        key: 'overdue',
        label: 'Overdue',
        value: s?.overdue ?? 0,
        variant: 'overdue',
        clickable: true,
        active: studyStatus === 'overdue',
        onClick: () => {
          setStudyStatus('overdue');
          setPage(1);
        },
      },
      {
        key: 'avgReporting',
        label: 'Avg reporting time',
        value: formatDurationMinutes(s?.avgReportingTimeMinutes ?? 0),
        subtitle: `TAT compliance ${formatPercent(s?.tatCompliancePercent ?? 0)}`,
      },
      {
        key: 'cancelled',
        label: 'Cancelled',
        value: s?.cancelled ?? 0,
        clickable: true,
        active: studyStatus === 'cancelled',
        onClick: () => {
          setStudyStatus('cancelled');
          setPage(1);
        },
      },
    ];
  }, [data?.summary, studyStatus]);

  const handleClear = () => {
    setFrom(defaultFromDate());
    setTo(localTodayIso());
    setModality('all');
    setDepartment('all');
    setRadiologistId('all');
    setPriority('all');
    setPatientType('all');
    setFacilityId('all');
    setWorkloadPeriod('daily');
    setStudyStatus('all');
    setPage(1);
  };

  const handleExport = async (format: AnalyticsExportFormat) => {
    if (format === 'pdf' && data) {
      const rows = data.studies.data
        .map(
          r =>
            `<tr><td>${r.accession}</td><td>${r.patient}</td><td>${r.modality}</td><td>${r.status}</td><td>${r.tatMinutes ?? ''}</td></tr>`
        )
        .join('');
      openAnalyticsPrintWindow(
        'Operational Dashboard',
        `<table><thead><tr><th>Accession</th><th>Patient</th><th>Modality</th><th>Status</th><th>TAT (min)</th></tr></thead><tbody>${rows}</tbody></table>`
      );
      return;
    }
    await exportOperationalDashboard(
      {
        from,
        to,
        facilityId,
        modality,
        department,
        radiologistId,
        priority,
        patientType,
        status: statusFilter,
        workloadPeriod,
      },
      format
    );
  };

  if (!canAccessPage(user, '/analytics/operational')) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  return (
    <PageLayout
      title="Operational dashboard"
      hideTitleInHeader
      breadcrumbs={[
        { label: 'Dashboard', path: '/admin-dashboard' },
        { label: 'Branch Admin' },
        { label: 'Analytics' },
        { label: 'RIS' },
        { label: 'Operational' },
      ]}
    >
      <div className="analytics-page">
        <div className="analytics-filter-bar analytics-filter-bar--operational">
          <AnalyticsDateInput
            label="From"
            value={from}
            max={to}
            onChange={setFrom}
          />
          <AnalyticsDateInput
            label="To"
            value={to}
            min={from}
            max={localTodayIso()}
            onChange={setTo}
          />
          {(data?.filterOptions?.facilities?.length ?? 0) > 1 ? (
            <div className="analytics-filter-field">
              <label>Facility</label>
              <select
                value={facilityId}
                onChange={e => setFacilityId(e.target.value)}
              >
                <option value="all">All facilities</option>
                {(data?.filterOptions?.facilities || []).map(f => (
                  <option
                    key={f.id}
                    value={String(f.id)}
                  >
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="analytics-filter-field">
            <label>Modality</label>
            <select
              value={modality}
              onChange={e => setModality(e.target.value)}
            >
              <option value="all">All modalities</option>
              {(data?.filterOptions?.modalities || []).map(m => (
                <option
                  key={m}
                  value={m}
                >
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Department</label>
            <select
              value={department}
              onChange={e => setDepartment(e.target.value)}
            >
              <option value="all">All departments</option>
              {(data?.filterOptions?.departments || []).map(d => (
                <option
                  key={d}
                  value={d}
                >
                  {d}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Radiologist</label>
            <select
              value={radiologistId}
              onChange={e => setRadiologistId(e.target.value)}
            >
              <option value="all">All radiologists</option>
              {(data?.filterOptions?.radiologists || []).map(r => (
                <option
                  key={r.id}
                  value={String(r.id)}
                >
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Priority</label>
            <select
              value={priority}
              onChange={e => setPriority(e.target.value)}
            >
              <option value="all">All priorities</option>
              <option value="Urgent">Urgent</option>
              <option value="VIP">VIP</option>
              <option value="MLC">MLC</option>
              <option value="Routine">Routine</option>
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Status</label>
            <select
              value={studyStatus}
              onChange={e => setStudyStatus(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="received">Received</option>
              <option value="inProgress">In progress</option>
              <option value="awaitingReview">Awaiting review</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="analytics-filter-field">
            <label>Patient type</label>
            <select
              value={patientType}
              onChange={e => setPatientType(e.target.value)}
            >
              <option value="all">All patient types</option>
              <option value="OPD">OPD</option>
              <option value="IPD">IPD</option>
              <option value="Emergency">Emergency</option>
            </select>
          </div>
          <div className="analytics-filter-actions">
            <button
              type="button"
              className="analytics-text-btn"
              onClick={handleClear}
            >
              Clear
            </button>
            <button
              type="button"
              className="analytics-text-btn"
              onClick={load}
            >
              Refresh
            </button>
            <AnalyticsExportMenu
              onExport={handleExport}
              disabled={loading}
            />
          </div>
        </div>

        {loading ? null : (
          <div className="analytics-refresh-row">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={e => setAutoRefresh(e.target.checked)}
              />{' '}
              Auto-refresh every 2 min
            </label>
            <span>
              {data?.filters.facilityName || '—'} · Last refreshed{' '}
              {data?.filters.lastRefreshedAt
                ? formatRefreshTime(data.filters.lastRefreshedAt)
                : '—'}
            </span>
          </div>
        )}

        {error ? (
          <div className="analytics-error">
            {error}{' '}
            <button
              type="button"
              className="analytics-text-btn"
              onClick={load}
            >
              Retry
            </button>
          </div>
        ) : null}

        {loading ? (
          <AnalyticsDashboardSkeleton layout="operational" />
        ) : (
          <>
            <KpiCardGrid
              items={kpiItems}
              layout="operational"
            />

            <div className="analytics-charts-row analytics-charts-row--operational">
              <AnalyticsGroupedBarChart
                title="Workload trend"
                data={(data?.workloadTrend || []).map(w => ({
                  label: w.date,
                  received: w.received,
                  completed: w.completed,
                  pending: w.pending,
                }))}
                periodControl={
                  <select
                    value={workloadPeriod}
                    onChange={e => setWorkloadPeriod(e.target.value)}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                }
              />
              <TatCompliancePanel
                data={
                  data?.tat || {
                    averageMinutes: 0,
                    medianMinutes: 0,
                    withinSla: 0,
                    exceedingSla: 0,
                    compliancePercent: 0,
                    notConfigured: 0,
                  }
                }
              />
            </div>

            <AnalyticsStudiesTable
              rows={data?.studies.data || []}
              page={data?.studies.pagination.page || page}
              pageSize={data?.studies.pagination.pageSize || pageSize}
              totalPages={data?.studies.pagination.totalPages || 0}
              totalItems={data?.studies.pagination.totalItems || 0}
              onPageChange={setPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </>
        )}
      </div>
    </PageLayout>
  );
}
